import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "autocompleteContainer",
    // Hidden fields to store parsed address
    "street",
    "street2",
    "city",
    "state",
    "zipcode",
    // Mobile templates (the <template> elements themselves)
    "mobileTemplate",
    "suggestionItemTemplate",
    "attributionTemplate"
  ]

  connect() {
    this.isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
    this.initAutocomplete()
  }

  async initAutocomplete() {
    if (!this.hasAutocompleteContainerTarget) return

    // Wait for Google Maps API to load with importLibrary available
    if (typeof google === 'undefined' || !google.maps || typeof google.maps.importLibrary !== 'function') {
      setTimeout(() => this.initAutocomplete(), 100)
      return
    }

    try {
      await google.maps.importLibrary('places')

      if (this.isMobile && this.hasMobileTemplateTarget) {
        // On mobile, use AutocompleteSuggestion API with a custom dropdown
        // to avoid the full-screen overlay that PlaceAutocompleteElement triggers
        this.initMobileAutocomplete()
      } else {
        this.initModernAutocomplete()
      }
    } catch (error) {
      console.error('Error initializing Google Places:', error)
    }
  }

  // Modern API using PlaceAutocompleteElement (desktop)
  initModernAutocomplete() {
    this.autocompleteElement = new google.maps.places.PlaceAutocompleteElement({
      includedRegionCodes: ['us']
    })

    this.autocompleteElement.placeholder = 'Lookup your practice\'s address*'
    this.autocompleteContainerTarget.appendChild(this.autocompleteElement)

    this.autocompleteElement.addEventListener('gmp-select', async (event) => {
      await this.handlePlaceSelect(event)
    })
  }

  // Mobile autocomplete using template + AutocompleteSuggestion API
  initMobileAutocomplete() {
    // Clone template and append to the autocomplete container
    const clone = this.mobileTemplateTarget.content.cloneNode(true)
    this.autocompleteContainerTarget.appendChild(clone)

    // Store direct DOM references (Stimulus targets aren't available immediately after clone)
    const wrapper = this.autocompleteContainerTarget.querySelector('.gp-mobile-wrapper')
    this.mobileInput = wrapper.querySelector('.gp-mobile-input')
    this.mobileClearBtn = wrapper.querySelector('.gp-mobile-clear-btn')
    this.mobileDropdown = wrapper.querySelector('.gp-mobile-dropdown')

    // Debounced input handler
    this.debounceTimer = null
    this.mobileInput.addEventListener('input', () => {
      clearTimeout(this.debounceTimer)
      const value = this.mobileInput.value.trim()
      this.mobileClearBtn.classList.toggle('is-visible', value.length > 0)
      if (value.length < 2) {
        this.mobileDropdown.style.display = 'none'
        return
      }
      this.debounceTimer = setTimeout(() => this.fetchSuggestions(value), 300)
    })

    // Clear button handler
    this.mobileClearBtn.addEventListener('click', () => this.clearMobileInput())

    // Close dropdown on outside click
    this.outsideClickHandler = (e) => {
      if (!wrapper.contains(e.target)) {
        this.mobileDropdown.style.display = 'none'
      }
    }
    document.addEventListener('click', this.outsideClickHandler)
  }

  clearMobileInput() {
    this.mobileInput.value = ''
    this.mobileClearBtn.classList.remove('is-visible')
    this.mobileDropdown.style.display = 'none'
    this.mobileInput.focus()
  }

  async fetchSuggestions(input) {
    try {
      const request = {
        input,
        includedRegionCodes: ['us'],
        language: 'en'
      }

      const { suggestions } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request)

      this.mobileDropdown.innerHTML = ''

      if (!suggestions || suggestions.length === 0) {
        this.mobileDropdown.style.display = 'none'
        return
      }

      suggestions.forEach(suggestion => {
        const pred = suggestion.placePrediction
        const fullText = pred.text.text
        const mainText = pred.mainText ? pred.mainText.text : fullText
        const secondaryText = pred.secondaryText ? pred.secondaryText.text : ''

        const item = this.suggestionItemTemplateTarget.content.cloneNode(true)
        item.querySelector('[data-role="main"]').textContent = mainText

        const secEl = item.querySelector('[data-role="secondary"]')
        if (secondaryText) {
          secEl.textContent = secondaryText
        } else {
          secEl.remove()
        }

        // Need a reference to the actual element for event listeners
        const itemEl = item.firstElementChild

        // Desktop / pointer: select on mousedown (preventDefault keeps input focus)
        itemEl.addEventListener('mousedown', (e) => {
          e.preventDefault()
          this.selectMobileSuggestion(suggestion)
        })

        // Touch: distinguish a tap from a scroll drag. Record the touch start
        // position and only select if the finger barely moved. Don't
        // preventDefault on touchstart/touchmove so the page can scroll freely
        // when the user drags over the dropdown to see the rest of it.
        let touchStartX = 0
        let touchStartY = 0
        let touchMoved = false
        itemEl.addEventListener('touchstart', (e) => {
          const t = e.touches[0]
          touchStartX = t.clientX
          touchStartY = t.clientY
          touchMoved = false
        }, { passive: true })
        itemEl.addEventListener('touchmove', (e) => {
          const t = e.touches[0]
          if (Math.abs(t.clientX - touchStartX) > 10 || Math.abs(t.clientY - touchStartY) > 10) {
            touchMoved = true
          }
        }, { passive: true })
        itemEl.addEventListener('touchend', (e) => {
          if (touchMoved) return // it was a scroll, not a tap
          e.preventDefault()
          this.selectMobileSuggestion(suggestion)
        }, { passive: false })

        this.mobileDropdown.appendChild(item)
      })

      // Google Maps attribution
      this.mobileDropdown.appendChild(
        this.attributionTemplateTarget.content.cloneNode(true)
      )

      this.mobileDropdown.style.display = 'block'
    } catch (error) {
      console.error('Error fetching suggestions:', error)
      this.mobileDropdown.style.display = 'none'
    }
  }

  async selectMobileSuggestion(suggestion) {
    this.mobileDropdown.style.display = 'none'

    try {
      const place = suggestion.placePrediction.toPlace()
      await place.fetchFields({
        fields: ['addressComponents', 'formattedAddress']
      })

      this.mobileInput.value = place.formattedAddress || ''

      const addressData = this.parseAddressComponents(place.addressComponents)
      this.populateHiddenFields(addressData)

      this.dispatch("addressSelected", {
        detail: {
          ...addressData,
          formattedAddress: place.formattedAddress
        }
      })
    } catch (error) {
      console.error('Error selecting place:', error)
    }
  }

  async handlePlaceSelect(event) {
    const { placePrediction } = event

    if (!placePrediction) {
      console.error("No place prediction available")
      return
    }

    try {
      const place = placePrediction.toPlace()
      await place.fetchFields({
        fields: ['addressComponents', 'formattedAddress']
      })

      const addressData = this.parseAddressComponents(place.addressComponents)
      this.populateHiddenFields(addressData)

      this.dispatch("addressSelected", {
        detail: {
          ...addressData,
          formattedAddress: place.formattedAddress
        }
      })

    } catch (error) {
      console.error('Error fetching place details:', error)
    }
  }

  parseAddressComponents(components) {
    const data = {
      streetNumber: '',
      streetName: '',
      city: '',
      state: '',
      zipcode: '',
      suite: ''
    }

    if (!components) return data

    components.forEach(component => {
      const types = component.types

      if (types.includes('street_number')) {
        data.streetNumber = component.longText || component.long_name || ''
      } else if (types.includes('route')) {
        data.streetName = component.longText || component.long_name || ''
      } else if (types.includes('locality')) {
        data.city = component.longText || component.long_name || ''
      } else if (types.includes('sublocality_level_1')) {
        if (!data.city) data.city = component.longText || component.long_name || ''
      } else if (types.includes('administrative_area_level_1')) {
        data.state = component.shortText || component.short_name || ''
      } else if (types.includes('postal_code')) {
        data.zipcode = component.longText || component.long_name || ''
      } else if (types.includes('subpremise')) {
        data.suite = component.longText || component.long_name || ''
      }
    })

    data.street = `${data.streetNumber} ${data.streetName}`.trim()

    return data
  }

  populateHiddenFields(data) {
    if (this.hasStreetTarget) this.streetTarget.value = data.street
    if (this.hasStreet2Target) this.street2Target.value = data.suite || ''
    if (this.hasCityTarget) this.cityTarget.value = data.city
    if (this.hasStateTarget) this.stateTarget.value = data.state
    if (this.hasZipcodeTarget) this.zipcodeTarget.value = data.zipcode

    // Trigger input event on hidden fields to notify sign-up-wizard controller
    if (this.hasStreetTarget) {
      this.streetTarget.dispatchEvent(new Event('input', { bubbles: true }))
    }
  }

  disconnect() {
    if (this.autocompleteElement) {
      this.autocompleteElement.remove()
    }
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }
    if (this.outsideClickHandler) {
      document.removeEventListener('click', this.outsideClickHandler)
    }
  }
}
